import { useMemo, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { createAxiosInstance, API_URL } from "../../../api";

export type StepCompletionStatus = {
  isCompleted: boolean;
  isOptional: boolean;
  hasErrors?: boolean;
};

export function useStepCompletion() {
  const profileUser = useSelector((state: any) => state.profile?.user || {});
  const { status: accountStatus } = useSelector((state: any) => state.main) || {};
  const userId = useSelector((state: any) => state.main?.userId);

  const {
    avatar,
    about,
    title,
    gender,
    online,
    faceToFace,
    qualifications,
    email,
    phone,
    isSubmitted,
    _id,
  } = profileUser;

  // Check if user has subjects (async - we'll need to fetch this)
  // For now, we'll track this separately
  const hasSubjects = useMemo(() => {
    // This will be set by fetching subjects in the sidebar
    return false; // Placeholder
  }, []);

  // Check billing status by fetching from API
  const [hasPaid, setHasPaid] = useState(false);

  useEffect(() => {
    if (!userId) {
      setHasPaid(false);
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.post(
          `${API_URL}/api/billings/hasPaid/${userId}`
        );

        const paidStatus = response.data?.paid;
        const isPaid = paidStatus === "Paid" || paidStatus === true;
        setHasPaid(isPaid);
      } catch (error: any) {
        console.error("Error checking payment status:", error);
        setHasPaid(false);
      }
    };

    checkPaymentStatus();

    // Listen for payment completion events
    const handlePaymentComplete = () => {
      checkPaymentStatus();
    };

    window.addEventListener('paymentCompleted', handlePaymentComplete);

    return () => {
      window.removeEventListener('paymentCompleted', handlePaymentComplete);
    };
  }, [userId]);

  const completions = useMemo(
    () => ({
      subjects: {
        isCompleted: hasSubjects,
        isOptional: false,
      },
      "tutoring-options": {
        isCompleted: Boolean(online || faceToFace),
        isOptional: false,
      },
      "tutor-profile": {
        isCompleted: Boolean(
          title && gender !== -1 && about && about !== "<p><br></p>" && avatar
        ),
        isOptional: false,
      },
      qualifications: {
        isCompleted: Object.keys(qualifications || {}).some(
          (k) => qualifications[k] !== -1
        ),
        isOptional: false,
      },
      "contact-info": {
        isCompleted: Boolean(email || phone),
        isOptional: true,
      },
      subscription: {
        isCompleted: hasPaid,
        isOptional: false,
      },
      submit: {
        isCompleted: Boolean(isSubmitted),
        isOptional: false,
      },
    }),
    [
      hasSubjects,
      online,
      faceToFace,
      title,
      gender,
      about,
      avatar,
      qualifications,
      email,
      phone,
      hasPaid,
      isSubmitted,
    ]
  );

  return completions;
}

